import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DataTable from "@/components/DataTable";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

const BLANK = { code: "", name: "", description: "", yearly_quota: 12, accrual_type: "yearly", is_paid: 1, carry_forward: 0, max_carry: 0, requires_document: 0, active: 1 };
const FLAGS = [["is_paid", "Paid leave"], ["carry_forward", "Carry forward unused days"], ["requires_document", "Document required"], ["active", "Active (employees can apply)"]];

function TypeDialog({ t, onClose, onSaved }) {
  const [f, setF] = useState(BLANK);
  useEffect(() => { if (t) setF({ ...BLANK, ...t }); }, [t]);
  const save = async () => {
    try { await api.post("/leave-types", f); toast.success("Leave type saved \u00b7 synced to admin.ohcampus.com"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!t} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white"><DialogHeader><DialogTitle>{f.id ? "Edit" : "New"} Leave Type</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Code</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="e.g. SL" data-testid="lt-code" /></div>
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Sick Leave" data-testid="lt-name" /></div>
          <div><Label>Days per year</Label><Input type="number" min="0" step="0.5" value={f.yearly_quota} onChange={(e) => setF({ ...f, yearly_quota: e.target.value })} data-testid="lt-quota" /></div>
          <div><Label>Accrual</Label>
            <select value={f.accrual_type} onChange={(e) => setF({ ...f, accrual_type: e.target.value })} className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm bg-white" data-testid="lt-accrual">
              {["yearly", "quarterly", "monthly", "none"].map((x) => <option key={x} value={x}>{x}</option>)}</select></div>
          <div className="col-span-2"><Label>Description</Label><Input value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="lt-desc" /></div>
          {FLAGS.map(([k, l]) => <label key={k} className="flex items-center gap-2 text-sm"><Switch checked={!!Number(f[k])} onCheckedChange={(v) => setF({ ...f, [k]: v ? 1 : 0 })} data-testid={`lt-${k}`} />{l}</label>)}
          {!!Number(f.carry_forward) && <div><Label>Max carry (days)</Label><Input type="number" min="0" value={f.max_carry} onChange={(e) => setF({ ...f, max_carry: e.target.value })} data-testid="lt-max-carry" /></div>}
        </div>
        <DialogFooter><Button onClick={save} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="lt-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LeaveTypes() {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/leave-types?all=1").then(({ data }) => setRows(data)).catch((e) => { setRows([]); toast.error(formatApiError(e.response?.data?.detail)); });
  useEffect(() => { load(); }, []);
  const columns = [
    { key: "code", label: "Code", render: (r) => <span className="font-mono font-bold text-[#312E81]">{r.code}</span> },
    { key: "name", label: "Name", render: (r) => <span className="font-semibold">{r.name}</span> },
    { key: "yearly_quota", label: "Days / year" },
    { key: "accrual_type", label: "Accrual" },
    { key: "is_paid", label: "Paid", render: (r) => (Number(r.is_paid) ? "Yes" : "No") },
    { key: "active", label: "Status", render: (r) => <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${Number(r.active) ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{Number(r.active) ? "Active" : "Inactive"}</span> },
    { key: "act", label: "", fixed: true, render: (r) => <Button size="sm" variant="ghost" onClick={() => setEdit(r)} data-testid={`lt-edit-${r.code}`}><Pencil className="w-4 h-4" /></Button> },
  ];
  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-slate-500">Leave types and yearly quotas are shared with the HR dashboard on admin.ohcampus.com. Changing a quota updates every employee's balance.</p>
        <Button onClick={() => setEdit({ ...BLANK })} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="lt-add"><Plus className="w-4 h-4 mr-1" /> Add Leave Type</Button>
      </div>
      <DataTable columns={columns} rows={rows} loading={rows === null} testid="leave-types-table" rowTestid="lt-row" columnsKey="leave_types" empty="No leave types" />
      <TypeDialog t={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </>
  );
}
